import React, { FunctionComponent } from 'react'
import { browserHistory } from 'react-router'
import ErrorMessage from '../../components/ErrorMessage'
import { logoutPath, workspacesPath } from '../../lib/path-helpers'
import MfaSecurityCheck from '../MfaSecurityCheck/components/MfaSecurityCheck'
import { SSOWorkspaceAccessDeniedCard } from './components/SSOWorkspaceAccessDeniedCard'
import useUser from '../../hooks/use-user'

/**
 * Reason for failed access to a workspace.
 */
export type AuthzAccessDeniedReason =
  | 'unspecified'
  | 'sso_required'
  | 'wrong_sso_connection'
  | 'mfa_required'
  | undefined

/**
 * Properties needed to render an alert given a workspace the current user does not have access to.
 */
interface WorkspaceAccessDeniedAppProps {
  accessDeniedReason?: AuthzAccessDeniedReason
  workspaceSlug: string
}

const navigateToWorkspacesButton = {
  children: 'View my workspaces',
  onClick() {
    browserHistory.replace(workspacesPath())
  }
}

const logOutUser = () => browserHistory.replace(logoutPath())

/**
 * When a user is denied access to a workspace, they are presented with relevant information and actions to take.
 */
const WorkspaceAccessDeniedApp: FunctionComponent<WorkspaceAccessDeniedAppProps> = ({
  accessDeniedReason,
  workspaceSlug
}) => {
  const { user } = useUser()

  switch (accessDeniedReason) {
    case 'sso_required':
    case 'wrong_sso_connection':
      const email = user ? user.email : ''
      return <SSOWorkspaceAccessDeniedCard email={email} workspaceSlug={workspaceSlug} />
    case 'mfa_required':
      return (
        <MfaSecurityCheck
          onDismiss={isMfaSetup => (isMfaSetup ? logOutUser() : undefined)}
          userId={user!.id}
          allowSkip={false}
          title="Multi-factor Authentication Required"
          description="This workspace requires multi-factor authentication. Multi-factor authentication adds an additional layer of security to your account by requiring more than just a password to login."
        />
      )
    case 'unspecified':
    default:
      return (
        <ErrorMessage
          title="Workspace access denied"
          messaging="You do not have access to this workspace."
          primaryButton={navigateToWorkspacesButton}
        />
      )
  }
}

export default WorkspaceAccessDeniedApp
